/**
 * Utility functions to convert between cron expressions and UI format
 * UI format: { frequency: string, interval: number, weekday: string, time: string }
 * Cron format: string (5-field format: minute hour dayOfMonth month dayOfWeek)
 * Note: Backend expects 5-field format (standard cron), not 6-field (with seconds)
 * Uses react-js-cron utilities for parsing and generating cron expressions
 */

import { converter } from 'react-js-cron';

export interface AlertDeliveryConfigUI {
  frequency: string;
  interval: number;
  weekday: string;
  time: string;
}

export interface AlertDeliveryConfigCron {
  cron: string;
}

export interface AlertsSettingsCron {
  criticalCron: string;
  highCron: string;
  mediumCron: string;
  lowCron: string;
  timezone: string;
}

// Constants
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

const DEFAULT_UI_CONFIG: AlertDeliveryConfigUI = {
  frequency: "Immediate",
  interval: 6,
  weekday: "Monday",
  time: "00:00"
};

/**
 * Parses cron expression parts using react-js-cron, handling both 5-field and 6-field formats
 */
function parseCronParts(cronExpression: string): {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
} | null {
  try {
    // react-js-cron's parseCronString returns an array of arrays: [[minutes], [hours], [daysOfMonth], [months], [daysOfWeek]]
    const parsed = converter.parseCronString(cronExpression);
    if (parsed && parsed.length >= 5) {
      // Convert arrays to cron string format
      const convertArrayToString = (arr: number[]): string => {
        if (!arr || arr.length === 0) return '*';
        if (arr.length === 1) return arr[0].toString();
        // For multiple values, create comma-separated list
        // For intervals, we'd need more complex logic, but for now just join
        return arr.join(',');
      };
      
      return {
        minute: convertArrayToString(parsed[0] || []),
        hour: convertArrayToString(parsed[1] || []),
        dayOfMonth: convertArrayToString(parsed[2] || []),
        month: convertArrayToString(parsed[3] || []),
        dayOfWeek: convertArrayToString(parsed[4] || [])
      };
    }
  } catch (error) {
    // Fallback to manual parsing if react-js-cron fails
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length === 5) {
      const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
      return { minute, hour, dayOfMonth, month, dayOfWeek };
    } else if (parts.length === 6) {
      // 6-field format with seconds: second minute hour dayOfMonth month dayOfWeek
      const [, minute, hour, dayOfMonth, month, dayOfWeek] = parts;
      return { minute, hour, dayOfMonth, month, dayOfWeek };
    }
  }
  
  return null;
}

/**
 * Formats hour and minute into time string (HH:mm)
 */
function formatTime(hour: number, minute: number): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

// Parses interval pattern from minute field (handles */X pattern and comma-separated lists for minutes)
function parseIntervalMinutesPattern(minute: string, hour: string): AlertDeliveryConfigUI | null {
  // Check for "*/X" pattern in minute field (e.g., "*/15 * * * *" means every 15 minutes)
  if (minute.startsWith("*/")) {
    const interval = parseInt(minute.substring(2));
    if (interval && interval >= 1 && interval <= 59) {
      // For minutes interval with */X pattern, it starts at minute 0
      const hourNum = hour === "*" ? 0 : parseInt(hour) || 0;
      const minuteNum = 0; // Start at minute 0 of the hour
      return {
        frequency: "Every X Minutes",
        interval,
        weekday: "Monday",
        time: formatTime(hourNum, minuteNum)
      };
    }
  }
  
  // Check for comma-separated list of minutes (e.g., "30,45 * * * *")
  // This represents every X minutes starting from a specific minute
  if (minute.includes(',') && hour === "*") {
    const minutes = minute.split(',').map(m => parseInt(m.trim())).filter(m => !isNaN(m) && m >= 0 && m < 60);
    if (minutes.length >= 2) {
      // Calculate interval from consecutive minutes
      const firstMinute = minutes[0];
      const secondMinute = minutes[1];
      const interval = secondMinute - firstMinute;
      
      // Verify it's a consistent interval
      const isValidInterval = interval > 0 && minutes.every((m, i) => 
        i === 0 || (m - minutes[i - 1]) === interval || (minutes[i - 1] + interval >= 60 && m === (minutes[i - 1] + interval) % 60)
      );
      
      if (isValidInterval && interval >= 1 && interval <= 59) {
        return {
          frequency: "Every X Minutes",
          interval,
          weekday: "Monday",
          time: formatTime(0, firstMinute)
        };
      }
    }
  }
  
  return null;
}

// Parses interval pattern from hour field (handles */X and comma-separated patterns)
function parseIntervalPattern(hour: string, minute: string): AlertDeliveryConfigUI | null {
  // Check for "*/X" pattern
  if (hour.startsWith("*/")) {
    const interval = parseInt(hour.substring(2)) || 6;
    const minuteNum = parseInt(minute) || 0;
    return {
      frequency: "Every X Hours",
      interval,
      weekday: "Monday",
      time: formatTime(0, minuteNum)
    };
  }
  
  // Check for comma-separated list (e.g., "0,6,12,18")
  if (hour.includes(',')) {
    const hours = hour.split(',').map(h => parseInt(h.trim())).filter(h => !isNaN(h));
    if (hours.length > 0) {
      const firstHour = hours[0];
      const secondHour = hours.length > 1 ? hours[1] : firstHour;
      const interval = secondHour - firstHour;
      
      // Verify it's a consistent interval
      const isValidInterval = interval > 0 && hours.every((h, i) => 
        i === 0 || (h - hours[i - 1]) === interval
      );
      
      if (isValidInterval) {
        const minuteNum = parseInt(minute) || 0;
        return {
          frequency: "Every X Hours",
          interval,
          weekday: "Monday",
          time: formatTime(firstHour, minuteNum)
        };
      }
    }
  }
  
  return null;
}

export function cronToUI(cronExpression: string): AlertDeliveryConfigUI {
  // Empty cron means immediate
  if (!cronExpression || cronExpression.trim() === "") {
    return { ...DEFAULT_UI_CONFIG };
  }

  // Use react-js-cron's parseCronString for better parsing
  let parsed: number[][] | null = null;
  try {
    parsed = converter.parseCronString(cronExpression);
  } catch (error) {
    // Fallback to manual parsing
    const cronParts = parseCronParts(cronExpression);
    if (!cronParts) {
      return { ...DEFAULT_UI_CONFIG };
    }
    return parseCronPartsToUI(cronParts);
  }

  if (!parsed || parsed.length < 5) {
    return { ...DEFAULT_UI_CONFIG };
  }

  const [minutes, hours, monthDays, months, weekDays] = parsed;

  // Check for "Every X minutes" pattern: requires all wildcards except minute
  // Pattern: */X * * * * (every X minutes) or comma-separated minutes
  if (monthDays.length === 0 && months.length === 0 && weekDays.length === 0 && hours.length === 0) {
    const intervalMinutesPattern = parseIntervalMinutesPatternFromArrays(minutes, hours);
    if (intervalMinutesPattern) {
      return intervalMinutesPattern;
    }
  }

  // Check for "Every X hours" pattern: requires all wildcards except hour
  if (monthDays.length === 0 && months.length === 0 && weekDays.length === 0) {
    // Special case: "0 * * * *" means every hour (interval = 1)
    if (hours.length === 0 && minutes.length > 0) {
      return {
        frequency: "Every X Hours",
        interval: 1,
        weekday: "Monday",
        time: formatTime(0, minutes[0])
      };
    }
    
    const intervalPattern = parseIntervalPatternFromArrays(hours, minutes);
    if (intervalPattern) {
      return intervalPattern;
    }
    
    // If not an interval pattern, treat as daily
    const hourNum = hours.length > 0 ? hours[0] : 0;
    const minuteNum = minutes.length > 0 ? minutes[0] : 0;
    return {
      frequency: "Daily",
      interval: 6,
      weekday: "Monday",
      time: formatTime(hourNum, minuteNum)
    };
  }

  // Check for weekly pattern: specific day of week with wildcards for date/month
  if (monthDays.length === 0 && months.length === 0 && weekDays.length > 0) {
    const dayIndex = weekDays[0];
    const weekday = WEEKDAYS[dayIndex] || "Monday";
    const hourNum = hours.length > 0 ? hours[0] : 0;
    const minuteNum = minutes.length > 0 ? minutes[0] : 0;
    
    return {
      frequency: "Weekly",
      interval: 6,
      weekday,
      time: formatTime(hourNum, minuteNum)
    };
  }

  // Default to immediate if we can't parse it
  return { ...DEFAULT_UI_CONFIG };
}

/**
 * Helper function to parse cron parts (from fallback manual parsing) to UI format
 */
function parseCronPartsToUI(cronParts: {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
}): AlertDeliveryConfigUI {
  const { minute, hour, dayOfMonth, month, dayOfWeek } = cronParts;

  // Check for "Every X minutes" pattern
  if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*" && hour === "*") {
    const intervalMinutesPattern = parseIntervalMinutesPattern(minute, hour);
    if (intervalMinutesPattern) {
      return intervalMinutesPattern;
    }
  }

  // Check for "Every X hours" pattern
  if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    // Special case: "0 * * * *" means every hour (interval = 1)
    if (hour === "*" && minute !== "*") {
      const minuteNum = parseInt(minute) || 0;
      return {
        frequency: "Every X Hours",
        interval: 1,
        weekday: "Monday",
        time: formatTime(0, minuteNum)
      };
    }
    
    const intervalPattern = parseIntervalPattern(hour, minute);
    if (intervalPattern) {
      return intervalPattern;
    }
    
    // If not an interval pattern, treat as daily
    const hourNum = parseInt(hour) || 0;
    const minuteNum = parseInt(minute) || 0;
    return {
      frequency: "Daily",
      interval: 6,
      weekday: "Monday",
      time: formatTime(hourNum, minuteNum)
    };
  }

  // Check for weekly pattern
  if (dayOfMonth === "*" && month === "*" && dayOfWeek !== "*") {
    const dayIndex = parseInt(dayOfWeek);
    const weekday = WEEKDAYS[dayIndex] || "Monday";
    const hourNum = parseInt(hour) || 0;
    const minuteNum = parseInt(minute) || 0;
    
    return {
      frequency: "Weekly",
      interval: 6,
      weekday,
      time: formatTime(hourNum, minuteNum)
    };
  }

  return { ...DEFAULT_UI_CONFIG };
}

/**
 * Parses interval pattern from minute arrays (using react-js-cron parsed data)
 */
function parseIntervalMinutesPatternFromArrays(minutes: number[], hours: number[]): AlertDeliveryConfigUI | null {
  if (minutes.length === 0 || hours.length > 0) return null;

  // Check if it's a regular interval pattern (every X minutes)
  // For intervals like */10, the library parses it as [0, 10, 20, 30, 40, 50]
  if (minutes.length > 1) {
    const interval = minutes[1] - minutes[0];
    // Verify it's a consistent interval
    const isValidInterval = interval > 0 && minutes.every((m, i) => 
      i === 0 || (m - minutes[i - 1]) === interval
    );
    
    if (isValidInterval && interval >= 1 && interval <= 59) {
      return {
        frequency: "Every X Minutes",
        interval,
        weekday: "Monday",
        time: formatTime(0, minutes[0])
      };
    }
  }

  return null;
}

/**
 * Parses interval pattern from hour arrays (using react-js-cron parsed data)
 */
function parseIntervalPatternFromArrays(hours: number[], minutes: number[]): AlertDeliveryConfigUI | null {
  if (hours.length === 0) return null;

  // Check if it's a regular interval pattern (every X hours)
  if (hours.length > 1) {
    const interval = hours[1] - hours[0];
    // Verify it's a consistent interval
    const isValidInterval = interval > 0 && hours.every((h, i) => 
      i === 0 || (h - hours[i - 1]) === interval
    );
    
    if (isValidInterval) {
      const minuteNum = minutes.length > 0 ? minutes[0] : 0;
      return {
        frequency: "Every X Hours",
        interval,
        weekday: "Monday",
        time: formatTime(hours[0], minuteNum)
      };
    }
  }

  return null;
}

/**
 * Generates cron expression for "Every X Minutes" frequency
 * Note: react-js-cron's getCronStringFromValues with 'minute' period doesn't handle intervals correctly,
 * so we use manual generation for interval patterns
 */
function generateIntervalMinutesCron(time: string, interval: number): string {
  const [, startMinute] = time.split(':').map(Number);
  const startMinuteValue = startMinute || 0;
  
  // Calculate all minutes when alerts should fire within an hour
  const minutes: number[] = [];
  let currentMinute = startMinuteValue;
  while (currentMinute < 60) {
    minutes.push(currentMinute);
    currentMinute += interval;
  }
  
  // If start time is at minute 0 and we have a full interval pattern, use simpler pattern
  if (startMinuteValue === 0 && minutes.length === 60 / interval) {
    return `*/${interval} * * * *`;
  }
  
  // Otherwise, use comma-separated minutes list
  // 5-field format: minute hour dayOfMonth month dayOfWeek (no seconds)
  const minutesStr = minutes.join(',');
  return `${minutesStr} * * * *`;
}

/**
 * Generates cron expression for "Every X Hours" frequency
 * Note: react-js-cron's getCronStringFromValues with 'hour' period doesn't handle intervals correctly,
 * so we use manual generation for interval patterns
 */
function generateIntervalCron(time: string, interval: number): string {
  const [startHour, startMinute] = time.split(':').map(Number);
  const startHourNum = startHour || 0;
  const minuteNum = startMinute || 0;
  const minuteStr = minuteNum.toString();
  
  // Calculate all hours when alerts should fire
  const hours: number[] = [];
  for (let h = startHourNum; h < 24; h += interval) {
    hours.push(h);
  }
  
  // Special case: if it starts at hour 0 and interval divides evenly into 24, use simpler pattern
  if (startHourNum === 0 && 24 % interval === 0) {
    return `${minuteStr} */${interval} * * *`;
  }
  
  // Otherwise, use comma-separated hours list
  // 5-field format: minute hour dayOfMonth month dayOfWeek (no seconds)
  const hoursStr = hours.join(',');
  return `${minuteStr} ${hoursStr} * * *`;
}

/**
 * Converts UI format to cron expression
 * @param uiConfig - UI format object
 * @returns Cron string
 */
export function uiToCron(uiConfig: AlertDeliveryConfigUI): string {
  // Immediate frequency returns empty cron
  if (uiConfig.frequency === "Immediate") {
    return "";
  }

  const [hour, minute] = uiConfig.time.split(':').map(Number);
  const hourStr = (hour || 0).toString();
  const minuteStr = (minute || 0).toString();

  // Every X Minutes pattern
  if (uiConfig.frequency === "Every X Minutes") {
    const interval = uiConfig.interval || 10;
    return generateIntervalMinutesCron(uiConfig.time, interval);
  }

  // Every X Hours pattern
  if (uiConfig.frequency === "Every X Hours") {
    const interval = uiConfig.interval || 6;
    return generateIntervalCron(uiConfig.time, interval);
  }

  // Daily pattern - use react-js-cron for generation
  if (uiConfig.frequency === "Daily") {
    try {
      return converter.getCronStringFromValues(
        'day',           // period
        undefined,       // months
        undefined,       // monthDays
        undefined,       // weekDays
        [hour || 0],     // hours
        [minute || 0],   // minutes
        false,           // humanizeValue
        undefined        // dropdownsConfig (optional)
      );
    } catch (error) {
      // Fallback to manual generation
      return `${minuteStr} ${hourStr} * * *`;
    }
  }

  // Weekly pattern - use react-js-cron for generation
  if (uiConfig.frequency === "Weekly") {
    const dayIndex = WEEKDAYS.indexOf(uiConfig.weekday as typeof WEEKDAYS[number]);
    const dayOfWeek = dayIndex >= 0 ? dayIndex : 1;
    try {
      return converter.getCronStringFromValues(
        'week',          // period
        undefined,       // months
        undefined,       // monthDays
        [dayOfWeek],     // weekDays (0=Sunday, 1=Monday, etc.)
        [hour || 0],     // hours
        [minute || 0],   // minutes
        false,           // humanizeValue
        undefined        // dropdownsConfig (optional)
      );
    } catch (error) {
      // Fallback to manual generation
      return `${minuteStr} ${hourStr} * * ${dayOfWeek}`;
    }
  }

  // Default to empty (immediate)
  return "";
}

/**
 * Converts an alerts settings object from cron format to UI format
 */
export function convertAlertsSettingsCronToUI(
  settings: AlertsSettingsCron
): Record<string, AlertDeliveryConfigUI> {
  return {
    critical: cronToUI(settings.criticalCron),
    high: cronToUI(settings.highCron),
    medium: cronToUI(settings.mediumCron),
    low: cronToUI(settings.lowCron)
  };
}

/**
 * Converts an alerts settings object from UI format to cron format
 */
export function convertAlertsSettingsUIToCron(
  settings: Record<string, AlertDeliveryConfigUI>,
  timezone: string
): AlertsSettingsCron {
  return {
    criticalCron: uiToCron(settings.critical),
    highCron: uiToCron(settings.high),
    mediumCron: uiToCron(settings.medium),
    lowCron: uiToCron(settings.low),
    timezone: timezone
  };
}

